import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import Select from 'react-select';
import { AsyncPaginate, reduceGroupedOptions } from 'react-select-async-paginate';
import {
	Collapse,
	Form,
	FormFeedback,
	FormGroup,
	Label,
} from 'reactstrap';

import {
	FormField,
	FIELD_TYPES,
	useFormValidation,
	tcFormValidators,
} from '@techchange/components';
import {
	A11yInput,
	A11yMenu,
	A11yOption,
	A11yLoadingMessage,
	Button,
	LearnMoreLink,
	DOCUMENTATION_LINKS,
	SCOPES,
	getLanguageOptions,
} from '@techchange/components';
import { messages } from '../../../i18n';

const propTypes = {

	/**
	 * The initial view for the form.
	 */
	view: PropTypes.oneOf([SCOPES.SESSION, SCOPES.ORGANIZATION]).isRequired,

	/**
	 * The initial data for the form.
	 */
	initialValues: PropTypes.shape({

		/**
		 * The base language for the source content.
		 */
		base_language: PropTypes.string.isRequired,

		/**
		 * The target languages for final translation of the content.
		 */
		target_languages: PropTypes.arrayOf(PropTypes.shape({

			label: PropTypes.string.isRequired,

			value: PropTypes.string.isRequired,

		})),

		/**
		 * True, if including pre-existing translation data for all the target languages.
		 */
		include_existing_translations: PropTypes.bool,

		/**
		 * True, if replacing the modified content in Lokalise.
		 */
		replace_modified: PropTypes.bool,

		/**
		 * The scope.
		 */
		scope: PropTypes.string,

		/**
		 * The scope ID.
		 */
		scope_id: PropTypes.string,

	}),

	/**
	 * The state related to the projects update API call to Lokalise.
	 */
	apiStatusProject: PropTypes.shape({

		/**
		 * The Lokalise translation project.
		 */
		project: PropTypes.shape({

			/**
			 * The unique ID (from Lokalise) for the project.
			 */
			project_id: PropTypes.string.isRequired,

		}),

		/**
		 * True, if the API call is still in progress.
		 */
		saving: PropTypes.bool.isRequired,

		/**
		 * True, if the API call has succeeded.
		 */
		success: PropTypes.bool.isRequired,

		/**
		 * True, if the API call has failed.
		 */
		error: PropTypes.bool.isRequired,

	}).isRequired,

	/**
	 * The state related to the files upload API call to Lokalise.
	 */
	apiStatusUploadFiles: PropTypes.shape({

		/**
		 * True, if the API call is still in progress.
		 */
		saving: PropTypes.bool.isRequired,

		/**
		 * The status of the uploaded files while in progress.
		 */
		savingStatus: PropTypes.objectOf(PropTypes.shape({

			/**
			 * The unique ID (from Lokalise) for the file upload.
			 */
			process_id: PropTypes.string.isRequired,

			/**
			 * The status (from Lokalise) for the file upload.
			 */
			status: PropTypes.string.isRequired,

			/**
			 * The status message (from Lokalise), if any, for the file upload.
			 */
			message: PropTypes.string,

		})).isRequired,

		/**
		 * True, if the API call has succeeded.
		 */
		success: PropTypes.bool.isRequired,

		/**
		 * True, if the API call has failed.
		 */
		error: PropTypes.bool.isRequired,

	}).isRequired,

	/**
	 * Function to handle loading the options for the base source content.
	 */
	onLoadBaseSourceOptions: PropTypes.func,

	/**
	 * Function to handle saving the form.
	 */
	onSave: PropTypes.func.isRequired,

};

/**
 * Shared component to render the form for uploading content to Lokalise for translation.
 *
 * @param {Object} props The props.
 * @returns {Object} The rendered component.
 */
const TranslationsUploadToLokaliseForm = (props) => {

	const {
		view,
		initialValues,
		apiStatusProject,
		apiStatusUploadFiles,
		onLoadBaseSourceOptions,
		onSave,
		...restProps
	} = props;

	const intl = useIntl();

	const [formData, setFormData] = useState({
		base_source: null,
		base_language: initialValues?.base_language,
		target_languages: initialValues?.target_languages || [],
		include_existing_translations:
			initialValues?.hasOwnProperty('include_existing_translations')
				? initialValues.include_existing_translations : false,
		replace_modified:
			initialValues?.hasOwnProperty('replace_modified')
				? initialValues.replace_modified : true,
		scope: initialValues?.scope,
		scope_id: initialValues?.scope_id,
	});

	const { inputValidation, validateFormData } = useFormValidation({
		base_source:
			view === SCOPES.ORGANIZATION ? [tcFormValidators.required] : [],
		base_language: [tcFormValidators.required],
		target_languages: [tcFormValidators.required],
	});

	const [showAdvanced, setShowAdvanced] = useState(false);

	const selectRefBaseSource = useRef(null);
	const selectRefTargetLanguages = useRef(null);

	const handleInputChange = useCallback((name, value) => {

		setFormData((prev) => ({ ...prev, [name]: value }));

		validateFormData({ [name]: value });

	}, [validateFormData]);

	const handleSubmit = useCallback(async (event) => {

		if (event) {
			event.preventDefault();
		}

		// Check to make sure that the form data is valid
		const isValid = validateFormData(formData);

		// If it is valid, submit the form data
		if (isValid) {
			await onSave(formData);
		}

	}, [formData, validateFormData, onSave]);

	// Get the option for the hardcoded base language for translation
	const baseLanguageOption = getLanguageOptions().find(
		(option) => option.value === formData.base_language
	);

	// Build the list of options for choosing the target language(s) for translation
	const targetLanguagesOptions = getLanguageOptions().filter(
		(option) => option.value !== formData.base_language
	);

	console.log(formData);

	return (
		<Form
			{...restProps}
			onSubmit={handleSubmit}>
			{view === SCOPES.ORGANIZATION &&
				<FormGroup>
					<Label for="base_source">
						{intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_LABEL_TEXT_BASE_SOURCE)}
					</Label>
					<AsyncPaginate
						id="tc-translations-upload-to-lokalise-form-base-source-field"
						name="base_source"
						className={
							`${inputValidation?.base_source?.invalid ? 'tc-react-select-input-invalid' : ''}`
						}
						classNamePrefix="tc-react-select"
						components={{
							Input: A11yInput,
							Menu: A11yMenu,
							Option: A11yOption,
							LoadingMessage: A11yLoadingMessage,
						}}
						value={formData.base_source}
						debounceTimeout={350}
						selectRef={selectRefBaseSource}
						placeholder={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_PLACEHOLDER_TEXT_BASE_SOURCE)}
						getOptionLabel={(option) => option.title}
						getOptionValue={(option) => option.id}
						loadOptions={onLoadBaseSourceOptions}
						reduceOptions={reduceGroupedOptions}
						captureMenuScroll={false}
						tabSelectsValue={false}
						defaultOptions
						onChange={(newSource) => {

							handleInputChange('scope', newSource.scope);
							handleInputChange('scope_id', newSource.scope_id);
							handleInputChange('base_language', newSource.language);
							handleInputChange('base_source', newSource);

						}} />
					{inputValidation?.base_source?.invalid &&
						<FormFeedback className="d-flex">
							<FormattedMessage {...messages.TC_COMPONENTS_FORM_ERROR_REQUIRED} />
						</FormFeedback>
					}
				</FormGroup>
			}
			<FormField
				id="tc-translations-upload-to-lokalise-form-base-language-field"
				type={FIELD_TYPES.SELECT}
				name="base_language"
				label={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_LABEL_TEXT_BASE_LANGUAGE)}
				placeholder={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_PLACEHOLDER_TEXT_BASE_LANGUAGE)}
				value={baseLanguageOption?.value}
				options={baseLanguageOption ? [baseLanguageOption] : []}
				error={inputValidation?.base_language?.error}
				isInvalid={inputValidation?.base_language?.invalid}
				disabled
				isEditing
				onChange={handleInputChange} />
			<FormGroup>
				<Label for="target_languages">
					{intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_LABEL_TEXT_TARGET_LANGUAGES)}
				</Label>
				<Select
					id="tc-translations-upload-to-lokalise-form-target-languages-field"
					name="target_languages"
					className={
						`${inputValidation?.target_languages?.invalid ? 'tc-react-select-input-invalid' : ''}`
					}
					classNamePrefix="tc-react-select"
					components={{
						Input: A11yInput,
						Menu: A11yMenu,
						Option: A11yOption,
						LoadingMessage: A11yLoadingMessage,
					}}
					selectRef={selectRefTargetLanguages}
					options={targetLanguagesOptions}
					defaultValue={formData.target_languages}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_PLACEHOLDER_TEXT_TARGET_LANGUAGES)}
					isDisabled={!formData.base_language}
					isClearable
					isMulti
					onChange={(newValue) => handleInputChange('target_languages', newValue)} />
				{inputValidation?.target_languages?.invalid &&
					<FormFeedback className="d-flex">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_ERROR_REQUIRED} />
					</FormFeedback>
				}
			</FormGroup>
			<FormGroup>
				<Button
					color="link"
					onClick={() => setShowAdvanced(!showAdvanced)}>
					{showAdvanced
						? <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_HIDE_ADVANCED_OPTIONS} />
						: <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_SHOW_ADVANCED_OPTIONS} />
					}
				</Button>
				<Collapse
					className="mt-2"
					isOpen={showAdvanced}>
					<FormField
						className="default-toggle"
						type={FIELD_TYPES.SWITCH}
						name="include_existing_translations"
						label={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_LABEL_TEXT_INCLUDE_EXISTING_TRANSLATIONS)}
						help={
							<div className="d-flex">
								<span>
									<FormattedMessage
										{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_HELPER_TEXT_INCLUDE_EXISTING_TRANSLATIONS}
										values={{
											languages: formData.target_languages
												.map((language) => language.label)
												.join(', ')
										}} />
								</span>
								<LearnMoreLink
									id="tc-translations-include-existing-translations-field-learn-more-btn"
									className="ms-1"
									aria-label={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_ARIA_LABEL_TEXT_INCLUDE_EXISTING_TRANSLATIONS)}
									href={DOCUMENTATION_LINKS.LOKALISE}
									title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_TEXT_LEARN_MORE)} />
							</div>
						}
						value={formData.include_existing_translations}
						isEditing
						isLabelInline
						onChange={handleInputChange} />
					<FormField
						className="default-toggle"
						type={FIELD_TYPES.SWITCH}
						name="replace_modified"
						label={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_LABEL_TEXT_REPLACE_MODIFIED)}
						help={
							<div className="d-flex">
								<span>
									<FormattedMessage
										{...messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_HELPER_TEXT_REPLACE_MODIFIED} />
								</span>
								<LearnMoreLink
									id="tc-translations-replace-modified-field-learn-more-btn"
									className="ms-1"
									aria-label={intl.formatMessage(messages.TC_COMPONENTS_TRANSLATIONS_LOKALISE_UPLOAD_FORM_FIELD_ARIA_LABEL_TEXT_REPLACE_MODIFIED)}
									href={DOCUMENTATION_LINKS.LOKALISE}
									title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_TEXT_LEARN_MORE)} />
							</div>
						}
						value={formData.replace_modified}
						isEditing
						isLabelInline
						onChange={handleInputChange} />
				</Collapse>
			</FormGroup>
			<FormGroup>
				<Button
					type="submit"
					color="primary"
					disabled={
						apiStatusProject.saving ||
						apiStatusUploadFiles.saving
					}>
					{(apiStatusProject.saving || apiStatusUploadFiles.saving)
						? <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SEND_LABEL_ACTIVE} />
						: <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SEND_LABEL} />
					}
				</Button>
			</FormGroup>
		</Form>
	);

};

TranslationsUploadToLokaliseForm.propTypes = propTypes;

export default TranslationsUploadToLokaliseForm;
